import React from "react"
import {Col, DatePicker, Row, Select, Spin, Tooltip} from "antd";
import IRestResource from "model/interface/api/IRestResource";
import IRepositoryService from "model/interface/IRepositoryService";
import {connect, RootStateOrAny} from "react-redux";
import selectors from "../../../redux/selectors";
import moment, {Moment} from "moment";
import IContentType from "../../../model/interface/dataStorage/IContentType";
import DataStorageService from "../../../model/service/dataStorage/DataStorageService";
import _ from "underscore";
import IViewItem from "../../../model/interface/dataStorage/view/IViewItem";
import {IViewGraphSettingsInterval} from "../../../model/interface/dataStorage/view/graph/IViewGraphSettings";
import {IActionResult} from "../../../model/service/dataStorage/ActionsService";
import ViewCustomFilters, {DefaultCustomFilters} from "./ViewCustomFilters";
import IRestServiceFilters from "../../../model/interface/api/IRestServiceFilters";
import IUser from "../../../model/interface/security/IUser";
import {IBaseViewProps} from "./ViewUnit";
import DataStorageHelper from "../../../utils/DataStorageHelper";
import IField from "../../../model/interface/dataStorage/IField";
import IPresenter from "../../../model/interface/dataStorage/IPresenter";
import ReactDOMServer from "react-dom/server";
import ViewEditButton from "./ViewEditButton";
import ViewGraphService from "../../../model/service/dataStorage/view/ViewGraphService";
import IRestServiceOptions from "../../../model/interface/api/IRestServiceOptions";
import {
    API_AGGREGATION_TYPE,
    API_AGGREGATION_TYPE_LIST,
    API_FILTER_TYPE,
    API_GROUP_BY_FORMAT
} from "../../../model/constants/ApiConstant";
import {DATE_FORMAT_YYYY_MM_DD} from "../../../model/constants/DateConstant";
import {MomentBuilder} from "../../../utils/MomentBuilder";
import {FilterOutlined, ReloadOutlined, StopOutlined} from "@ant-design/icons";
import PieChart from "./graph/PieChart";
import XYChart from "./graph/XYChart";
import ViewPersonalEditButton from "./ViewPersonalEditButton";
import {GraphXAxis} from "../../../model/interface/dataStorage/view/graph/IViewGraphOptions";
import ViewSchemeActions from "./ViewSchemeActions";
import Button from "../../shared/button/Button";
import ViewFilterIsland from "./tools/ViewFilterIsland";
import LocaleText from "../settings/dictionary/LocaleText";
import IRestServiceAggregations from "../../../model/interface/api/IRestServiceAggregations";
import {RestAggregation} from "../../../model/interface/api/IRestServiceCollectionResponse";
import ViewTitle from "./ViewTitle";
import {VIEW_AGGREGATION_PLACEMENT} from "../../../model/interface/dataStorage/view/table/IAggregationOptions";
import Aggregations from "./statistics/Aggregations";

const {RangePicker} = DatePicker;

interface IChartCategory {
    label: string
    min?: any
    max?: any,
    resource: IRestResource,
    fieldName: string
}

export interface IChartDataSet {
    name: string,
    data: any
}

interface IState {
    results: Array<IRestResource>,
    total: number,
    loading: boolean,
    filters: IRestServiceFilters
    interval: IViewGraphSettingsInterval
    intervalMin?: Moment,
    intervalMax?: Moment
    intervalLength: number
    customFilters?: IRestServiceFilters
    filterIsland: IRestServiceFilters
    showFilterIsland?: boolean
    aggregations?: RestAggregation[]
}

interface IProps extends IBaseViewProps {
    findOneByFullClassName: (fullClassName: string) => IRepositoryService
    findOneByContentType: (contentType: IContentType) => IRepositoryService,
    findContentTypeByUuid: (uuid: string) => IContentType
    getText: (code: string, fallBack?: string) => string
    user: IUser
}

class ViewGraph extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            results: [] as Array<IRestResource>,
            filters: {},
            filterIsland: {},
            loading: false,
            total: 0,
            customization: {
                items: null
            },
            customizationDraft: null,
            activeContentTypes: [] as number[],
            interval: "month",
            intervalLength: 12,
            customFilters: ViewCustomFilters.getDefault(props.settings.customFilters)[this.getContentType().fullClassName],
            ...this.presetState()
        }
    }

    componentDidMount() {
        this.load().then()
    }

    load = (): Promise<void> => {
        const {filters, viewUnit, findOneByContentType} = this.props
        const {customFilters, intervalMin, intervalMax, interval, filterIsland} = this.state
        const {
            graphXAxis,
            graphType,
            graphGroup,
            graphAggregation,
            graphXAxisField,
            graphAggregationFields
        } = viewUnit.options
        const field = this.getField(graphXAxisField)
        const contentType = this.getContentType()
        const service = findOneByContentType(contentType)
        this.setState({loading: true})
        let aggregations: IRestServiceAggregations = {}
        if (viewUnit.options?.aggregations) {
            Object.entries(viewUnit.options.aggregations).forEach(([uuid, item]) => {
                const field = _.findWhere(service.getFields(), {name: item.field});
                if (field) {
                    aggregations[uuid] = {
                        field: field.name,
                        type: item.type
                    }
                }
            })
        }
        const params: IRestServiceOptions = {
            filters: {...filters, ...customFilters, ...filterIsland},
            groupBy: {
                x: {
                    field: field.name, format: graphXAxis === ViewGraphService.X_AXIS_INTERVAL ? {
                        day: API_GROUP_BY_FORMAT.DATE_DAY,
                        week: API_GROUP_BY_FORMAT.DATE_WEEK,
                        month: API_GROUP_BY_FORMAT.DATE_MONTH,
                        quarter: API_GROUP_BY_FORMAT.DATE_QUARTER,
                        year: API_GROUP_BY_FORMAT.DATE_YEAR,
                    }[interval] : ''
                }
            },
            cache: false,
            limit: 0,
            aggregations,
            view: viewUnit.view
        }

        switch (graphType) {
            case ViewGraphService.TYPE_PIE:
            case ViewGraphService.TYPE_DONUT:
                // Use configured field/type if provided, fallback to count on 'id'
                const pieFieldUuid = viewUnit.options.graphAggregationFields?.[0];
                const pieField = pieFieldUuid ? this.getField(pieFieldUuid) : null;
                const aggregationType = graphAggregation || API_AGGREGATION_TYPE.SUM;

                params.aggregations = {
                    ...params.aggregations,
                    pieMetric: {
                        field: pieField ? pieField.name : 'id',
                        type: pieField ? aggregationType : API_AGGREGATION_TYPE.COUNT,
                        groupBy: true
                    }
                }
                break;
            case ViewGraphService.TYPE_BAR:
            case ViewGraphService.TYPE_LINE:
                (viewUnit.options.graphAggregationFields?.length ? viewUnit.options.graphAggregationFields : viewUnit.items.map(item => item.field)).forEach(item => {
                    params.aggregations = {
                        ...params.aggregations,
                        aggregation: {
                            field: this.getField(item).name,
                            type: graphAggregation,
                            groupBy: true
                        }
                    }
                })
                if (graphGroup && this.getField(graphGroup)) {
                    params.groupBy = {
                        ...params.groupBy,
                        group: {
                            field: this.getField(graphGroup).name
                        }
                    }
                }
        }

        if (field && graphXAxis === ViewGraphService.X_AXIS_INTERVAL && intervalMax && intervalMin) {
            params.filters = {
                ...params.filters,
                from: {
                    field: field.name,
                    type: API_FILTER_TYPE.GREATER,
                    value: intervalMin.clone().add(-1, 'day').format(DATE_FORMAT_YYYY_MM_DD)
                },
                to: {
                    field: field.name,
                    type: API_FILTER_TYPE.LESSER,
                    value: intervalMax.clone().add(1, 'day').format(DATE_FORMAT_YYYY_MM_DD)
                }
            }
        }

        return this.getService().collectionList(params).then(({count, results, aggregations}: any) => {
            return new Promise(resolve => {
                this.setState({
                    results,
                    total: count,
                    loading: false,
                    aggregations: aggregations
                }, resolve)
            })
        })
    }

    getService = (): DataStorageService => {
        return this.props.findOneByContentType(this.getContentType()) as DataStorageService
    }

    getContentType() {
        const {findContentTypeByUuid, viewUnit} = this.props
        return findContentTypeByUuid(viewUnit.contentTypes[0])
    }

    presetState() {
        const {settings, state} = this.props
        let previousState: any | IState = {}
        if (settings.graphIntervalLength) {
            previousState.intervalLength = settings.graphIntervalLength
        }
        if (settings.graphIntervals) {
            previousState.interval = state.graphInterval || settings.graphIntervals[0]
            previousState.intervalMax = moment().endOf(previousState.interval)
            previousState.intervalMin = moment().subtract(previousState.intervalLength - 1, previousState.interval)
                .startOf(previousState.interval)
        }
        if (settings.filterIsland && settings.filterIslandSaveState && state.filterIsland) {
            previousState.filterIsland = state.filterIsland[this.getContentType().uuid] || {}
            if (Object.keys(previousState.filterIsland).length) {
                previousState.showFilterIsland = true
            }
        }
        return previousState
    }

    saveState() {
        const {saveState, state, settings} = this.props
        const {interval, filterIsland} = this.state
        let newState = {
            graphInterval: interval,
            ...state
        }
        if (settings.filterIslandSaveState && filterIsland) {
            newState.filterIsland = {[this.props.viewUnit.contentTypes[0]]: filterIsland}
        }
        saveState({...newState}).then()
    }

    doAction = (result?: IActionResult) => {
        if (result) {
            return this.refresh()
        }
        return Promise.resolve()
    }

    refresh = (): Promise<void> => {
        return new Promise(resolve => {
            this.setState({results: []}, () => resolve(this.load()))
        })
    }

    onFilterIslandChange = (filters: IRestServiceFilters) => {
        this.setState(state => ({
            filterIsland: filters
        }), () => {
            this.saveState()
            this.refresh()
        })
    }

    showFilterIsland = () => {
        this.setState(state => ({showFilterIsland: !state.showFilterIsland}))
    }

    clearFilterIsland = () => {
        this.setState({filterIsland: {}}, () => {
            this.refresh()
        })
    }

    buildCategories = (): IChartCategory[] => {
        const categories: IChartCategory[] = []
        const {settings, viewUnit} = this.props
        const {
            interval, intervalLength, intervalMin, intervalMax, results
        } = this.state
        const {graphType, graphXAxis, graphXAxisInterval, graphXAxisField, graphFieldLabelsOrder} = viewUnit.options

        switch (graphType) {
            case(ViewGraphService.TYPE_BAR):
            case(ViewGraphService.TYPE_LINE):
            case(ViewGraphService.TYPE_PIE):
            case(ViewGraphService.TYPE_DONUT):
                switch (graphXAxis) {
                    case(ViewGraphService.X_AXIS_INTERVAL):
                        const intervalList = ViewGraphService.intervalFormats()
                        const {delta, format} = intervalList[interval || graphXAxisInterval]

                        let min = intervalMin?.clone() || (intervalMax || moment())?.clone()
                            .add(1 - (settings.graphIntervalLength || intervalLength), delta)
                        let max = intervalMax?.clone() || intervalMin?.clone()
                            .add((settings.graphIntervalLength || intervalLength) - 1, delta) || moment()
                        if (!min || !max) {
                            break;
                        }
                        if (delta === "week" && (intervalMin || intervalMax) && min !== max) { // TODO Refactor maybe ?? tricky !!!...
                            max = max.endOf(delta).add(1, "d")
                            min = min.endOf(delta).add(1, "d")
                        }
                        let current = min.clone().startOf(delta)
                        while (current < max) {
                            categories.push({
                                label: current.format(format),
                                min: current.clone(),
                                max: current.clone().add(1, delta),
                                resource: current,
                                fieldName: this.getField(graphXAxisField).name
                            });
                            current.add(1, delta)
                        }
                        break
                    case(ViewGraphService.X_AXIS_FIELD):
                        const field = this.getField(graphXAxisField)
                        results.filter((value, index, self) => self.indexOf(value) === index)
                            .forEach(result => {
                                categories.push({
                                    label: ReactDOMServer.renderToString(<>{this.buildFieldValue(field, result)}</>),
                                    resource: result,
                                    fieldName: field.name
                                })
                            })
                        break
                    default:
                        throw new Error('Unsupported graphXAxis: ' + graphXAxis)
                }
                break;
            default:
                throw new Error('Unsupported graph type: ' + graphType)
        }

        return categories.sort((a, b) => {
            if (graphFieldLabelsOrder) {
                const labelA = typeof a.resource[a.fieldName] === 'object' ? (a.resource[a.fieldName][graphFieldLabelsOrder] || '') : String(a);
                const labelB = typeof b.resource[b.fieldName] === 'object' ? (b.resource[b.fieldName][graphFieldLabelsOrder] || '') : String(b);
                return (labelA + '').localeCompare(labelB);
            }
            const labelA = typeof a === 'object' ? (a.resource[a.fieldName]?.name || '') : String(a);
            const labelB = typeof b === 'object' ? (b.resource[b.fieldName]?.name || '') : String(b);
            return (labelA + '').localeCompare(labelB);
        })
    }

    getField(uuid: string) {
        const contentType = this.getService().getContentType()
        const field = _.findWhere(contentType.fields, {uuid: uuid})
        if (!field) {
            throw new Error('Field not found by id: ' + uuid)
        }
        return field
    }

    getPresenter(field: IField, item?: IViewItem) {
        const {viewUnit} = this.props
        const {graphGroupPresenter} = viewUnit.options
        let presenter: null | IPresenter = null
        if (field.mode === 'relation') {
            const service = this.props.findOneByFullClassName(field.targetEntity!)
            if (graphGroupPresenter) {
                presenter = service.getPresenter(graphGroupPresenter) || service.getDefaultPresenter()
            }
        }
        if (!presenter) {
            presenter = {
                type: 'label',
                name: 'default',
                label: 'Výchozí',
                options: {
                    label: "#" + field.name
                }
            } as IPresenter
        }
        presenter.options = {...item?.options?.fieldOptions, ...item?.options, ...presenter.options, ...field.options}
        return presenter
    }

    buildSeries = () => {
        let series: IChartDataSet[] = []
        const {viewUnit, getText} = this.props
        const {results, interval} = this.state
        const {
            graphGroup,
            graphType,
            graphXAxisField,
            graphXAxis,
            graphOverallAggregations,
            graphFieldLabelsOrder
        } = viewUnit.options

        const contentType = this.getService().getContentType()
        const field = this.getField(graphXAxisField)
        const item = viewUnit.items.find(i => i.field === graphXAxisField)

        switch (graphType) {
            case(ViewGraphService.TYPE_BAR):
            case(ViewGraphService.TYPE_LINE):
                const fields = viewUnit.options.graphAggregationFields?.length ? viewUnit.options.graphAggregationFields : viewUnit.items.map(item => item.field)
                fields.filter(i => i !== graphGroup).forEach(fieldUuid => {
                    const itemField = _.findWhere(contentType.fields, {uuid: fieldUuid})!

                    if (graphGroup) {
                        const groupField = _.findWhere(contentType.fields, {uuid: graphGroup})!
                        const groupItem = viewUnit.items.find(i => i.field === graphGroup)!

                        // Unique groups
                        const distinctGroupValues = results
                            .sort((a, b) => {
                                if (graphFieldLabelsOrder) {
                                    const labelA = typeof a.resource[a.fieldName] === 'object' ? (a.resource[a.fieldName][graphFieldLabelsOrder] || '') : String(a);
                                    const labelB = typeof b.resource[b.fieldName] === 'object' ? (b.resource[b.fieldName][graphFieldLabelsOrder] || '') : String(b);
                                    return (labelA + '').localeCompare(labelB);
                                }
                                const labelA = typeof a === 'object' ? (a.name || '') : String(a);
                                const labelB = typeof b === 'object' ? (b.name || '') : String(b);
                                return (labelA + '').localeCompare(labelB);
                            })
                            .map(result => result[groupField.name])
                            .filter((value, index, self) => {
                                if (!value) return false;
                                const currentKey = typeof value === 'object' ? (value.uuid || value.id) : value;
                                return self.findIndex(item => {
                                    if (!item) return false;
                                    const itemKey = typeof item === 'object' ? (item.uuid || item.id) : item;
                                    return itemKey === currentKey;
                                }) === index;
                            })

                        distinctGroupValues.forEach(graphGroupValue => {
                            series.push({
                                data: this.buildBarData(field, itemField, {[groupField.name]: graphGroupValue}),
                                name: ReactDOMServer.renderToString(<>
                                        {getText(itemField.label || itemField.name) + ' - '}
                                        {this.buildFieldValue(groupField, {[groupField.name]: graphGroupValue}, groupItem)}
                                    </>
                                )
                            })
                        })

                        // --- NEW OVERALL AGGREGATION CALCULATIONS ---
                        if (graphOverallAggregations && Array.isArray(graphOverallAggregations)) {
                            graphOverallAggregations.forEach((aggType) => {

                                // We compute a brand new timeline series mapping across every interval category directly
                                const overallData = this.buildCategories().map(category => {
                                    // 1. Filter raw database results down to ONLY records falling inside this month/interval
                                    const matchRecords = results
                                        .filter(result => {
                                            switch (graphXAxis) {
                                                case ViewGraphService.X_AXIS_FIELD:
                                                    return result[field.name] === category.resource[field.name];
                                                case ViewGraphService.X_AXIS_INTERVAL:
                                                    const intervalValue = MomentBuilder.build(result[field.name]);
                                                    return intervalValue && intervalValue.isBetween(category.min, category.max, interval, '[)');
                                            }
                                            return true;
                                        })
                                        // Map down to the dynamic matching data property metrics keys (e.g. "amount0")
                                        .map(result => itemField && Number(result[itemField.name + '0']))
                                        .filter(val => !isNaN(val) && val !== null && val !== undefined);

                                    // 2. Run your specific mathematical metric over this month's raw global cross-section
                                    switch (aggType) {
                                        case API_AGGREGATION_TYPE.SUM:
                                            return matchRecords.reduce((s, v) => s + v, 0);

                                        case API_AGGREGATION_TYPE.AVG:
                                            // Mathematical average of the records this month
                                            const avg = matchRecords.length ? (matchRecords.reduce((s, v) => s + v, 0) / matchRecords.length) : 0;
                                            return Math.round((avg + Number.EPSILON) * 100) / 100;

                                        case API_AGGREGATION_TYPE.MIN:
                                            return matchRecords.length ? Math.min(...matchRecords) : 0;

                                        case API_AGGREGATION_TYPE.MAX:
                                            return matchRecords.length ? Math.max(...matchRecords) : 0;

                                        default:
                                            return 0;
                                    }
                                });

                                const aggLabel = API_AGGREGATION_TYPE_LIST.find(t => aggType === t.value);

                                series.push({
                                    data: overallData,
                                    name: `${getText(itemField.label || itemField.name)} - ${getText(aggLabel?.label || '', aggLabel?.labelFallback)}`
                                });
                            });
                        }
                        // --------------------------------------------

                    } else {
                        series.push({
                            data: this.buildBarData(field, itemField),
                            name: getText(field.label || field.name)
                        })
                    }
                })
                break;
            case(ViewGraphService.TYPE_PIE):
            case(ViewGraphService.TYPE_DONUT):
                series = this.buildPieSeries(graphXAxis, results, field, interval, item)
                break
            default:
                throw new Error('Unsupported graph type: ' + graphType)
        }
        return series
    }

    buildPieSeries(graphXAxis: GraphXAxis, results: IRestResource[], field: IField, interval: IViewGraphSettingsInterval, item?: IViewItem) {
        const series: IChartDataSet[] = [] as IChartDataSet[];
        const { graphAggregationFields } = this.props.viewUnit.options;

        // Resolve the aggregated target field (e.g. 'price'), NOT the group-by field ('category')
        const targetFieldUuid = graphAggregationFields?.[0];
        const targetField = targetFieldUuid ? this.getField(targetFieldUuid) : null;

        this.buildCategories().forEach(category => {
            let result: IRestResource | undefined;
            switch (graphXAxis) {
                case ViewGraphService.X_AXIS_FIELD:
                    result = category.resource;
                    break;
                case ViewGraphService.X_AXIS_INTERVAL:
                    result = results.find(r => {
                        const intervalValue = MomentBuilder.build(r[field.name]);
                        return intervalValue && intervalValue.isBetween(category.min, category.max, interval, '[)');
                    });
                    break;
            }

            // Look for explicit backend metric key 'pieMetric0', then dynamic target field 'price0', then 'id0' fallback
            const rawValue = result
                ? (result['pieMetric0'] ?? (targetField ? result[`${targetField.name}0`] : undefined) ?? result['id0'] ?? 0)
                : 0;

            series.push({
                data: Number(rawValue) || 0,
                name: ReactDOMServer.renderToString(<>{this.buildFieldValue(field, category.resource, item)}</>)
            });
        });

        return series;
    }

    buildFieldValue(field: IField, value: any, item?: IViewItem) {
        const {history} = this.props
        return value[field.name] !== null && value[field.name] !== undefined ? DataStorageHelper.buildFieldValue(field, value, history, this.getPresenter(field, item)) :
            <StopOutlined/>
    }

    buildBarData = (xField: IField, field?: IField, conditions?: any): {} => {
        const {viewUnit} = this.props
        const {results, interval} = this.state
        const {
            graphXAxis
        } = viewUnit.options

        return this.buildCategories().map(category => {
            const values: any = results
                .filter(result => {
                    // --- UPDATED MATCHING LOGIC ---
                    if (!conditions) return true;

                    return Object.entries(conditions).every(([key, condValue]) => {
                        const resultValue = result[key];

                        // If both are objects (relations), compare their unique identifiers
                        if (resultValue && condValue && typeof resultValue === 'object' && typeof condValue === 'object') {
                            const resKey = (resultValue as any).uuid || (resultValue as any).id;
                            const condKey = (condValue as any).uuid || (condValue as any).id;
                            return resKey === condKey;
                        }

                        // Fallback to primitive matching
                        return resultValue === condValue;
                    });
                    // ------------------------------
                })
                .filter(result => {
                    switch (graphXAxis) {
                        case ViewGraphService.X_AXIS_FIELD:
                            return result[xField.name] === category.resource[xField.name]
                        case ViewGraphService.X_AXIS_INTERVAL:
                            const intervalValue = MomentBuilder.build(result[xField.name])
                            return intervalValue && intervalValue.isBetween(category.min, category.max, interval, '[)')
                    }
                    return true;
                })
                .map(result => field && result[field.name + '0'])

            // Sum all matching records for this category interval instead of just taking the first one [0]
            return values.reduce((sum: number, val: any) => sum + (Number(val) || 0), 0);
        })
    }

    setIntervalMinMax = (intervalMinMax: [min: Moment, max: Moment]) => {
        if (intervalMinMax) {
            const [min, max] = intervalMinMax
            this.setState(state => ({
                intervalMin: (max && min > max ? null : min)?.startOf(state.interval),
                intervalMax: (min && max < min ? null : max)?.endOf(state.interval),
            }), this.load)
        } else {
            this.setState({intervalMin: undefined, intervalMax: undefined}, this.load)
        }
    }

    disableMaxLengthInterval = (current: Moment) => {
        const {interval, intervalMin, intervalMax, intervalLength} = this.state
        const maxLength = (this.props.settings.graphIntervalLength || intervalLength) - 1
        if (intervalMin && !intervalMax) {
            return current > intervalMin.clone().add(maxLength, interval) || intervalMin > current
        }
        if (!intervalMin && intervalMax) {
            return intervalMax.clone().add(-maxLength, interval) > current || intervalMax < current
        }
        if (intervalMin && intervalMax) {
            return (current < intervalMin && current < intervalMax.clone().add(-maxLength, interval))
                || (current > intervalMax && current > intervalMin.clone().add(maxLength, interval))
        }
        return false
    }

    setInterval = (interval: IViewGraphSettingsInterval) => {
        this.setState({interval, intervalMin: undefined, intervalMax: undefined}, this.load)
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any) {
        if (this.props.settings.graphIntervals?.length === 1
            && this.state.interval !== this.props.settings.graphIntervals[0]) {
            setInterval(this.props.settings.graphIntervals[0])
        }
        if (prevProps.reload !== this.props.reload && this.props.reload) {
            this.refresh().then()
        }
    }

    onCustomFilterChange = (filters?: DefaultCustomFilters) => {
        this.setState({customFilters: filters?.[this.getContentType().fullClassName]}, this.load)
    }

    buildGraph = () => {
        const {viewUnit, getText} = this.props
        const {graphType} = viewUnit.options
        const categories = this.buildCategories().map(category => getText(category.label))
        const series = this.buildSeries()
        const colors = ViewGraphService.buildColors(viewUnit.options.colorScheme)
        switch (graphType) {
            case (ViewGraphService.TYPE_BAR):
            case (ViewGraphService.TYPE_LINE):
                return <XYChart viewUnit={viewUnit} colors={colors} series={series} categories={categories}
                                type={graphType}/>
            case (ViewGraphService.TYPE_PIE):
            case (ViewGraphService.TYPE_DONUT):
                return <PieChart viewUnit={viewUnit} colors={colors} series={series}
                                 labels={categories} type={graphType}/>
        }
    }


    render() {
        const {settings, viewUnit, history, match, permissions, insideTab, standalone} = this.props
        const {loading, interval, intervalMin, intervalMax, filterIsland, showFilterIsland, aggregations} = this.state


        return (
            <div>
                <ViewTitle hideTitle={insideTab || !standalone} viewUnit={viewUnit}>
                    <Aggregations match={match} history={history} contentType={this.getContentType()}
                                  placement={VIEW_AGGREGATION_PLACEMENT.TOP}
                                  aggregations={aggregations} options={viewUnit.options?.aggregations}/>
                    <Row gutter={[6, 6]} className={'mb-2'}>
                        <ViewSchemeActions onFinish={this.doAction} contentType={this.getContentType()}
                                           permissions={permissions} viewUnit={viewUnit} match={match}
                                           history={history}/>
                        <Col>
                            <Tooltip title={<LocaleText code={'view.reload'} fallback={"Znovu načíst data"}/>}>
                                <Button onClick={this.refresh} style={{verticalAlign: "middle"}}
                                        icon={<ReloadOutlined/>} size={"small"}
                                />
                            </Tooltip>
                        </Col>
                        <ViewPersonalEditButton {...this.props} />
                        <ViewEditButton {...this.props} />
                        {Object.values(settings.filterIsland || {}).some(e => e.some(d => d.enabled && !d.defaultVisible)) && (
                            <Row>
                                <Col>
                                    <Button type={Object.keys(filterIsland).length ? 'info' : undefined} size={"small"}
                                            onClick={this.showFilterIsland}
                                            icon={<FilterOutlined/>}/>
                                </Col>
                                {Object.keys(filterIsland).length > 0 && <Col>
                                    <Button type={"link"} size={"small"} onClick={this.clearFilterIsland}>
                                        <LocaleText code={'view.filters.cancel'} fallback={'Zrušit filtry'}/>
                                    </Button>
                                </Col>}
                            </Row>
                        )}
                    </Row>
                </ViewTitle>
                <Row gutter={[6, 6]}
                     className={(settings.graphIntervals && settings.graphIntervals.length > 1) || (settings.graphIntervalRange && interval) || settings.customFilters ? 'mb-2' : ''}>
                    {settings.graphIntervals && settings.graphIntervals.length > 1 && (
                        <Col>
                            <Select value={interval} placeholder={'Zvolte interval'} onChange={this.setInterval}
                                    defaultActiveFirstOption={true}>
                                {ViewGraphService.buildIntervals()
                                    .filter(interval => settings.graphIntervals?.includes(interval.value))
                                    .map(interval => (
                                        <Select.Option key={interval.value} value={interval.value}>
                                            <LocaleText code={interval.label} fallback={interval.labelFallback}/>
                                        </Select.Option>
                                    ))}
                            </Select>
                        </Col>
                    )}
                    {settings.graphIntervalRange && interval && (
                        <Col>
                            <RangePicker placeholder={['od', 'do']} disabledDate={this.disableMaxLengthInterval}
                                         picker={interval === 'day' ? 'date' : interval}
                                         allowEmpty={[true, true]}
                                         value={[intervalMin || null, intervalMax || null]}
                                         onCalendarChange={this.setIntervalMinMax as any}/>
                        </Col>
                    )}
                    {settings.customFilters && (
                        <ViewCustomFilters filters={settings.customFilters} onChange={this.onCustomFilterChange}/>
                    )}
                </Row>
                <Aggregations match={match} history={history} contentType={this.getContentType()}
                              placement={VIEW_AGGREGATION_PLACEMENT.MIDDLE}
                              aggregations={aggregations} options={viewUnit.options?.aggregations}/>
                <Row hidden={!loading} className={'position-absolute w-100 h-100'}
                     style={{background: "rgba(255,255,255, 0.7)", zIndex: 10}}
                     justify={"center"} align={"middle"}>
                    <Spin size={"large"}/>
                </Row>
                <Row className={'w-100'}>
                    <ViewFilterIsland collapsed={!showFilterIsland}
                                      filters={settings.filterIsland?.[this.getContentType().uuid]}
                                      defaultValues={filterIsland}
                                      viewUnit={viewUnit} onChange={this.onFilterIslandChange}
                                      contentType={this.getContentType()}/>
                    <div className={'flex-grow-1'}>
                        {this.buildGraph()}
                    </div>
                </Row>
                <Aggregations match={match} history={history} contentType={this.getContentType()}
                              placement={VIEW_AGGREGATION_PLACEMENT.BOTTOM}
                              aggregations={aggregations} options={viewUnit.options?.aggregations}/>
            </div>
        )
    }
}


const mapStateToProps = (state: RootStateOrAny) => {
    return {
        findOneByContentType: (contentType: IContentType) => selectors.services.findOneByContentType(state, contentType),
        findOneByFullClassName: (fullClassName: string) => selectors.services.findOneByFullClassName(state, fullClassName),
        findContentTypeByUuid: (uuid: string) => selectors.contentTypes.findOneBy(state, 'uuid', uuid),
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback),
        user: state.setup.user
    }
}

export default connect(mapStateToProps)(ViewGraph)